import * as React from 'react';
import { Platform, StyleSheet, Text } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import 'react-native-get-random-values';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { enableScreens } from 'react-native-screens';
import { Provider } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { debug, log, logError } from './utils/logger';

import MainContainer from './navigation/MainContainer';
import Store from './store/Store';
import { EnvVars } from './constants/EnvConfig';
import { StatsigProviderExpo } from '@statsig/expo-bindings';
import ErrorBoundary from './components/errorBoundaries/ErrorBoundary';
import { ErrorScreen } from './screens/ErrorScreen';
import { PlentifulServiceContextProvider } from './components/services/PlentifulServiceContext';
import * as Updates from 'expo-updates';
import Constants from 'expo-constants';
import { ExpoModulesProvider } from './providers/ExpoModulesProvider';
import { TranslationProvider } from './providers/TranslationProvider';

let Sentry;
if (Platform.OS === 'web') {
  Sentry = require('@sentry/react');
} else {
  Sentry = require('@sentry/react-native');
}

debug('hello, world');
log('uuid', { uuid: uuidv4() });

enableScreens();

// disable font scaling for Text components
// https://stackoverflow.com/a/51414341/6118172
Text.defaultProps = Text.defaultProps || {};
Text.defaultProps.allowFontScaling = false;

// Hotfix for react-native-reanimated see: https://github.com/software-mansion/react-native-reanimated/issues/3355#issuecomment-1518942329
if (typeof window !== 'undefined') {
  window._frameTimestamp = null;
}

if (EnvVars.useSentry) {
  // initialize sentry
  Sentry.init({
    dsn: EnvVars.sentryDsn,
    environment: EnvVars.sentryEnv,
    debug: EnvVars.sentryEnv != 'production',
  });
}

const styles = StyleSheet.create({
  view: {
    flex: 1,
  },
});

const restartApp = async () => {
  if (Platform.OS === 'web') {
    location.reload();
  } else {
    try {
      await Updates.reloadAsync();
    } catch (e) {
      logError('Failed to reload app:', e);
    }
  }
};

function App({
  MainContainerComponent = MainContainer,
  StatsigProviderComponent = StatsigProviderExpo,
  ErrorScreenComponent = ErrorScreen,
} = {}) {
  const expoModules = {
    Updates,
    Constants,
  };

  const mainContainerWithErrorBoundary = (
    <ErrorBoundary fallback={<ErrorScreenComponent restartCallback={restartApp} />}>
      <TranslationProvider>
        <ExpoModulesProvider modules={expoModules}>
          <PlentifulServiceContextProvider envVars={EnvVars}>
            <GestureHandlerRootView style={styles.view}>
              <MainContainerComponent />
            </GestureHandlerRootView>
          </PlentifulServiceContextProvider>
        </ExpoModulesProvider>
      </TranslationProvider>
    </ErrorBoundary>
  );

  return (
    <Provider store={Store}>
      <SafeAreaProvider>
        <ErrorBoundary fallback={mainContainerWithErrorBoundary}>
          <StatsigProviderComponent
            sdkKey={EnvVars.statsigSdkKey}
            user={{}} //keeping the user anonymous until they've logged in; a stableId is tied to each device and can be used for experimentation
            options={{ environment: { tier: EnvVars.statsigEnv } }}
          >
            {mainContainerWithErrorBoundary}
          </StatsigProviderComponent>
        </ErrorBoundary>
      </SafeAreaProvider>
    </Provider>
  );
}

export default App;
